import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { deviceName } from "@/lib/upload";
import { useAuth, homeFor } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { Eyebrow } from "@/components/common";

export const CodeInput = ({ value, onChange, testId, autoFocus }) => (
  <input data-testid={testId} type="password" className="mr-input text-center text-2xl tracking-[0.5em] tabular" inputMode="numeric" autoComplete="current-password"
    maxLength={6} value={value} onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))} autoFocus={autoFocus} />
);

function ForgotForm({ onBack }) {
  const [identifier, setIdentifier] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-code", { identifier });
      setMsg(data.message);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
      <p className="text-sm text-mr-text2">Saisissez votre e-mail ou votre numéro : nous vous envoyons par e-mail un lien pour choisir un nouveau code.</p>
      <input data-testid="forgot-identifier-input" className="mr-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="vous@entreprise.ci ou 07 00 00 00 00" autoFocus />
      <button data-testid="forgot-submit-button" className="mr-btn-primary w-full" disabled={identifier.length < 5}>Recevoir le lien</button>
      {msg && <p className="text-sm text-mr-accent" data-testid="forgot-message">{msg}</p>}
      {error && <p className="text-sm text-mr-danger">{error}</p>}
      <button type="button" className="mr-link text-sm" onClick={onBack} data-testid="forgot-back-button">Retour à la connexion</button>
    </form>
  );
}

export default function Login({ console: isConsole = false }) {
  const nav = useNavigate();
  const { setMe } = useAuth();
  const { settings } = useConfig();
  const [identifier, setIdentifier] = useState("");
  const [code, setCode] = useState("");
  const [forgot, setForgot] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { identifier, code, device: deviceName(), scope: isConsole ? "console" : "membre" });
      setMe(data);
      nav(isConsole ? "/console" : homeFor(data), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="max-w-md mx-auto px-5 py-16 lg:py-24 mr-fade-up">
      <Eyebrow className="mb-4">{isConsole ? "Console d'administration" : "Espace membre"}</Eyebrow>
      <h1 className="mr-h2 sm:text-4xl">{isConsole ? "Connexion console" : "Connexion"}</h1>
      <p className="text-mr-text2 mt-3 mb-8 text-[15px]">{isConsole ? "Réservé aux administrateurs, modérateurs et trésoriers du club." : settings["texts.login_help"]}</p>
      <div className="mr-card p-6">
        {forgot ? <ForgotForm onBack={() => setForgot(false)} /> : (
          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="mr-label">E-mail ou numéro de téléphone</span>
              <input data-testid="login-identifier-input" className="mr-input" autoComplete="username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="vous@entreprise.ci ou 07 00 00 00 00" autoFocus />
            </label>
            <label className="block">
              <span className="mr-label">Code personnel (6 chiffres)</span>
              <CodeInput testId="login-code-input" value={code} onChange={setCode} />
            </label>
            <button data-testid="login-submit-button" className="mr-btn-primary w-full" disabled={busy || identifier.length < 5 || code.length !== 6}>
              {busy ? "Connexion…" : "Se connecter"} <ArrowRight size={16} />
            </button>
            {error && <p data-testid="login-error" className="text-sm text-mr-danger">{error}</p>}
            <button type="button" data-testid="login-forgot-button" className="mr-link text-sm" onClick={() => setForgot(true)}>Code oublié ?</button>
          </form>
        )}
      </div>
      {!isConsole && (
        <div className="text-sm text-mr-muted mt-6 space-y-2">
          <p>Vous avez reçu une invitation ? <Link to="/rejoindre" className="text-mr-accent hover:underline" data-testid="login-join-link">Créer mon compte</Link></p>
          <p>Pas encore invité ? <Link to="/demande-invitation" className="text-mr-accent hover:underline" data-testid="login-invitation-link">Demander une invitation</Link></p>
        </div>
      )}
    </section>
  );
}
